import {
  ChangeEventHandler,
  useState,
  useCallback,
  useEffect,
  useRef
} from 'react';
import { SurveyCakeLogicEngine, validateFile } from '@surveycake/logic-engine';
import {
  UseSubjectMetaSelector,
  UseSubjectAnswersSelector,
  useEngineInstance,
  useSubjectId,
  useSubjectMeta,
  useSubjectAnswers
} from '@surveycake/react-logic-engine';
import { Message } from '@surveycake/rc';
import { useSurveyConfig } from 'app/core/survey-config';
import { S3 } from 'app/modules/file-storage';
import { GoPageAndSubmitDisabledService } from 'app/modules/survey';
import { errorMap } from 'app/modules/subject';
import { addTimestampToFilename } from './utils';

const metaSelector: UseSubjectMetaSelector<boolean> = meta =>
  meta.fileuploadDisable;

export function useShowFileUploadAnswer() {
  const id = useSubjectId();
  return !useSubjectMeta(id, metaSelector);
}

function useFileUploadAnswerPercentage() {
  return useState(0);
}

function useFileUploadAnswerIsUploadingRef() {
  return useRef(false);
}

const answersSelector: UseSubjectAnswersSelector<string> = answers =>
  answers.payload[0] as string;

function useFileUploadAnswerFileName(
  id: number,
  uploadingRef: ReturnType<typeof useFileUploadAnswerIsUploadingRef>,
  [percentage, setPercentage]: ReturnType<typeof useFileUploadAnswerPercentage>
) {
  const fileName = useSubjectAnswers(id, answersSelector);

  useEffect(() => {
    if (fileName && percentage !== 100 && !uploadingRef.current) {
      setPercentage(100);
    } else if (!fileName && percentage !== 0) {
      setPercentage(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileName]);

  return fileName;
}

function useFileUploadAnswerUpload(
  engine: SurveyCakeLogicEngine,
  id: number,
  uploadingRef: ReturnType<typeof useFileUploadAnswerIsUploadingRef>,
  setPercentage: ReturnType<typeof useFileUploadAnswerPercentage>[1]
) {
  const { wording } = useSurveyConfig();
  const {
    lock,
    unlock
  } = GoPageAndSubmitDisabledService.useFileUploadLockMethods();

  return useCallback<ChangeEventHandler<HTMLInputElement>>(
    async event => {
      const file = event.target.files && event.target.files[0];

      if (!file) {
        return;
      }

      const fileName = addTimestampToFilename(file.name);
      const answer = { payload: [fileName], meta: { file } };

      const subjectMessageError = validateFile(
        answer,
        engine.getSubjectData(id)
      );

      // https://fox.25sprout.com:10088/surveycake/pm/issues/1530
      if (subjectMessageError) {
        const resolveFunction = errorMap[subjectMessageError.type];
        if (resolveFunction) {
          Message.error(resolveFunction(wording, subjectMessageError), {
            duration: 2000
          });
        }
        return;
      }

      setPercentage(0);

      uploadingRef.current = true;

      try {
        lock();
        await S3.upload(file, fileName, setPercentage);
        await engine.setAnswer(id, answer);
        unlock();
      } catch (error) {
        Message.error(wording.subject.fileupload.status.upload_failed, {
          duration: 2000
        });
      }

      uploadingRef.current = false;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [engine, id]
  );
}

function useFileUploadAnswerRemove(
  engine: SurveyCakeLogicEngine,
  id: number,
  setPercentage: ReturnType<typeof useFileUploadAnswerPercentage>[1]
) {
  return useCallback(() => {
    setPercentage(0);
    engine.setAnswer(id, { payload: [] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine, id]);
}

export function useFileUploadAnswer() {
  const engine = useEngineInstance();
  const id = useSubjectId();
  const uploadingRef = useFileUploadAnswerIsUploadingRef();
  const [percentage, setPercentage] = useFileUploadAnswerPercentage();
  const fileName = useFileUploadAnswerFileName(id, uploadingRef, [
    percentage,
    setPercentage
  ]);
  const upload = useFileUploadAnswerUpload(
    engine,
    id,
    uploadingRef,
    setPercentage
  );
  const remove = useFileUploadAnswerRemove(engine, id, setPercentage);

  return { fileName, percentage, onChange: upload, onRemove: remove };
}
